import { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { twJoin } from '@beerntea/pitcher-ui';
import VideoPlayer from '@pages/Usa/components/VideoPlayer.tsx';
import { TourLocation } from '@pages/Usa/components/TourLocation.tsx';
import type { UsatourDate } from '@pages/Usa/UsaPage.tsx';
import CampaignSignup from '@pages/Usa/components/CampaignSignup.tsx';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TourDatesListItem } from '@pages/Usa/components/TourDatesListItem.tsx';

export const UsaContent = ({ tourDates = [], fase }: { tourDates: UsatourDate[]; fase: number }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSpot, setActiveSpot] = useState<UsatourDate | null>(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [focusedSpotId, setFocusedSpotId] = useState<number | string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(max-width: 639px)', () => {
      const tl = gsap.timeline({ repeat: -1 });
      tourDates.forEach((loc) => {
        const item = scrollContainerRef.current;
        tl.to(item, {
          scrollTo: {
            x: (item!.scrollWidth * loc.x) / 100 - item!.clientWidth / 2,
            y: (item!.scrollHeight * loc.y) / 100 - item!.clientHeight / 2,
          },
          duration: 1.6,
          // delay: 0.6,
          ease: 'slow(0.7,0.7,false)',
          onStart: () => setFocusedSpotId(loc.id),
        });
      });

      const firstLoc = tourDates[0];
      if (firstLoc) {
        const item = scrollContainerRef.current;
        tl.to(item, {
          scrollTo: {
            x: (item!.scrollWidth * firstLoc.x) / 100 - item!.clientWidth / 2,
            y: (item!.scrollHeight * firstLoc.y) / 100 - item!.clientHeight / 2,
          },
          duration: 1.6,
          // delay: 0.6,
          ease: 'slow(0.7,0.7,false)',
          onStart: () => setFocusedSpotId(firstLoc.id),
        });
      }

      let timer: NodeJS.Timeout;
      const pauseAnimation = () => {
        tl.pause();
        clearTimeout(timer);
        timer = setTimeout(() => {
          tl.play();
        }, 3000);
      };

      const container = scrollContainerRef.current;
      container?.addEventListener('touchstart', pauseAnimation, { passive: true });
      container?.addEventListener('mousedown', pauseAnimation);
      container?.addEventListener('wheel', pauseAnimation, { passive: true });

      return () => {
        tl.kill();
        clearTimeout(timer);
        container?.removeEventListener('touchstart', pauseAnimation);
        container?.removeEventListener('mousedown', pauseAnimation);
        container?.removeEventListener('wheel', pauseAnimation);
      };
    });
  }, [tourDates]);

  useEffect(() => {
    // Trigger fade-in after mount
    const timer = setTimeout(() => setIsLoaded(true), 800); // slight delay for effect
    return () => clearTimeout(timer);
  }, []);

  const onClick = (tourDate: UsatourDate) => {
    console.log('Clicked tour date:', tourDate, fase);
    setActiveSpot(tourDate);
    if (fase === 2) setSignupOpen(true);
    if (fase >= 3) setIsVisible(true);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mouseEnter = (_tourDate: UsatourDate) => {};
  const mouseLeave = () => {};

  const handleListItemClick = (tourDate: UsatourDate) => {
    if (fase === 4 && tourDate.url) {
      window.location.href = tourDate.url;
      return;
    }
    setActiveSpot(tourDate);
    setSignupOpen(true);
  };

  const closeTourInfo = () => {
    setIsVisible(false);
    setShowAll(false);
  };

  const isMobile = false; // window.innerWidth < 639;
  const w = !isMobile ? undefined : fase < 2 ? '100wv' : 100 + 'vh';
  const h = !isMobile ? undefined : fase < 2 ? 56 + 'wv' : 100 * 0.5625 + 'vh';

  return (
    <div
      className={twJoin(
        'tw-relative  sm:tw-pt-[60%] tw-overflow-hidden',
        isLoaded
          ? 'tw-opacity-100 tw-transition-opacity tw-duration-[1500ms]'
          : 'tw-opacity-0 tw-transition-opacity tw-duration-[1500ms]',
      )}
    >
      {signupOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-bg-black/90 tw-overflow-y-auto">
          <div className="tw-flex tw-items-center tw-justify-center tw-min-w-full tw-min-h-full">
            <div className="tw-m-auto tw-bg-white tw-w-full tw-max-w-2xl tw-p-8 tw-relative tw-border tw-border-gray-200">
              <button
                onClick={() => {
                  setSignupOpen(false);
                  if (isFormSubmitted) {
                    navigate('/');
                  }
                }}
                className="tw-absolute tw-top-4 tw-right-4 tw-text-black"
              >
                <X size={24} />
              </button>
              {/* Show selected tour date info in the signup panel */}
              <CampaignSignup
                onSuccess={() => setIsFormSubmitted(true)}
                tourId={activeSpot?.id}
                location={activeSpot?.location}
                tourDate={activeSpot}
              />
            </div>
          </div>
        </div>
      )}
      {fase === 1 && (
        <div className="tw-fixed tw-bottom-10 tw-left-1/2 -tw-translate-x-1/2 tw-z-50">
          <button
            onClick={() => setSignupOpen(true)}
            className={twJoin(
              'tw-bg-white tw-text-black tw-px-8 tw-py-4 tw-text-xl tw-font-bold tw-uppercase',
              'max-sm:tw-py-2 max-sm:tw-px-4 max-sm:tw-text-base',
            )}
          >
            REGISTER NOW
          </button>
        </div>
      )}
      <div
        className={twJoin(
          "tw-bg-[url('/uploads/assets/0/static/site/img/usa_2026/hardwell.png')] tw-bg-cover tw-bg-top tw-bg-no-repeat",
          'tw-pointer-events-none',
          'tw-h-full tw-pt-[85%]',
          'sm:tw-hidden',
          // 'max-sm:tw-bg-left-bottom max-sm:tw-z-10',
        )}
      />
      <div
        className={twJoin(
          'sm:tw-absolute tw-top-0 tw-w-full tw-h-full',
          // 'max-sm:tw-pt-[30%]',
          'max-sm:tw-pt-2',
          'max-sm:tw-bg-center',
        )}
      >
        <div
          className={twJoin(
            "tw-bg-[url('/uploads/assets/0/static/site/img/usa_2026/hardwell.png')] tw-bg-cover tw-bg-right-bottom tw-bg-no-repeat",
            'tw-pointer-events-none',
            'tw-absolute tw-right-0 tw-bottom-0 tw-w-1/2 tw-h-2/3 sm:tw-h-full',
            'sm:-tw-right-[10%]',
            'max-sm:tw-hidden',
            // 'max-sm:tw-bg-left-bottom max-sm:tw-z-10',
          )}
        />
        {/*<div className="tw-bg-gradient-to-r tw-from-black tw-to-transparent tw-w-full tw-h-full tw-relative tw-right-1/3" />*/}
        <img
          id={'logo'}
          alt="Hardwell USA Run"
          src={'/uploads/assets/0/static/site/img/usa_2026/Hardwell-USA-Run-logomark.png'}
          className={twJoin(
            'sm:tw-absolute',
            'sm:tw-top-[5%] sm:tw-left-[15%] sm:tw-w-[40%]',
            'max-sm:tw-w-[85%]',
            'max-sm:tw-mx-auto max-sm:tw-block',
          )}
        />
        <div
          ref={scrollContainerRef}
          className={twJoin(
            'sm:tw-absolute sm:-tw-left-[5%] sm:tw-bottom-0 sm:tw-w-[82%]',
            // 'max-sm:tw-overflow-scroll no-scrollbar',
          )}
        >
          {/* factor 0,5625*/}
          <div
            className={twJoin('tw-relative', 'sm:tw-overflow-hidden', 'tw-mix-blend-screen', 'tw-left-4')}
            style={{
              width: w,
              height: h,
            }}
          >
            <VideoPlayer
              upload_id="40cd8d1495d0c4f69722fa9b47b23ba5"
              play={false}
              className={'tw-mix-blend-screen sm:-tw-m-[1px]'}
            />
            {fase >= 2 &&
              tourDates.map((tourDate) => (
                <TourLocation
                  key={tourDate.id}
                  tourDate={tourDate}
                  onClick={() => onClick(tourDate)}
                  onMouseEnter={() => mouseEnter(tourDate)}
                  onMouseLeave={() => mouseLeave()}
                  isFocused={activeSpot?.id === tourDate.id || focusedSpotId === tourDate.id}
                />
              ))}
          </div>
        </div>
      </div>
      {isVisible && (
        <button
          type="button"
          aria-label="Close tour info"
          onClick={closeTourInfo}
          className="tw-fixed tw-inset-0 tw-z-40 tw-bg-black/30"
        />
      )}
      <div
        id={'tour-date-info'}
        className={twJoin(
          'tw-z-50 tw-bottom-0 md:tw-left-1/2 md:-tw-translate-x-1/2',
          'tw-w-full tw-max-w-full md:tw-max-w-[50%] tw-bg-black tw-text-white',
          'tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out',
          isVisible ? 'tw-translate-y-0' : 'tw-translate-y-full',
          showAll
            ? 'tw-fixed tw-inset-x-0 tw-max-h-[100dvh] tw-overflow-y-auto tw-overscroll-contain'
            : 'tw-absolute tw-h-auto',
        )}
      >
        {!showAll && activeSpot && fase >= 3 && (
          <div className="tw-p-4 tw-relative">
            <TourDatesListItem
              tourDate={activeSpot}
              passRef={() => {}}
              onClick={() => handleListItemClick(activeSpot)}
            />
            <button
              onClick={() => setShowAll(true)}
              className="tw-mt-4 tw-w-full tw-py-2 tw-bg-white tw-text-black tw-font-bold tw-uppercase tw-font-display tw-text-xs hover:tw-bg-gray-200 tw-transition-colors"
            >
              Show Full Tour ({tourDates.length})
            </button>
          </div>
        )}

        {showAll && (
          <div className="tw-p-4">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <h3 className="tw-text-xl tw-font-bold tw-font-display">All Tour Dates</h3>
              <button
                onClick={() => setShowAll(false)}
                className="tw-text-sm tw-underline"
              >
                <X size={24} />
              </button>
            </div>
            <div className="tw-space-y-4">
              {tourDates.map((date) => (
                <TourDatesListItem
                  key={date.id}
                  tourDate={date}
                  passRef={() => {}}
                  onClick={() => handleListItemClick(date)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
